import { AudioSource } from "../model/audio-source.interface";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
import { IRegionPositions, ITimeUpdateEvent, NgWaveformComponent } from 'ng-waveform';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { IRegionPositionsAndDuration } from './model/region-positions-and-duration.interface';

// Variables innerHTML waveform
const currentTimeInnerWave = '_currentTime';
const savedCurrentTimeInnerWave = '_savedCurrentTime';

@Component({
  selector: 'app-waveform',
  templateUrl: './waveform.component.html',
  styleUrls: ['./waveform.component.scss']
})
export class WaveformComponent implements OnInit, OnDestroy {
  @Input() regions: Observable<IRegionPositions>;
  @Input() srcAudioValue: Observable<any>;
  @Input() messageNoAudio: Observable<string>;
  @Input() tabIndex: number;

  @Output() regionChange = new EventEmitter<IRegionPositionsAndDuration>();
  @Output() previousHour = new EventEmitter<boolean>(false);
  @Output() nextHour = new EventEmitter<boolean>(false);
  @Input() selectedLinesOnTableInteraction: AudioSource[];

  @Output() clearSelection = new EventEmitter<boolean>(false);

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  isLocal = environment.showTelemetry;

  @ViewChild('waveform', { static: false }) waveform: NgWaveformComponent;
  play = false;
  isLoaded = false;
  trackLoadTime: number;
  renderingTime: number;
  duration: number;
  currentTime: ITimeUpdateEvent;
  regionPositions: IRegionPositions;

  displayCurrentTime: string;
  displayDuration: string;

  isAudioQueried = false;

  isRegionCtrl = new FormControl(true);
  regionStartCtrl = new FormControl(0);
  regionEndCtrl = new FormControl(0);

  useRegion = true;
  src: string;
  hour: string;
  start: string;
  end: string;
  selectDuration: string;
  messageNoAudioAvailable = 'No Audio Available';
  regionsChangedFromTable = false;

  constructor() { }

  ngOnInit() {
    this.isRegionCtrl.valueChanges.subscribe(
      (value) => (this.useRegion = value)
    );
    this.regionStartCtrl.valueChanges.subscribe((value) => {
      if (!this.waveform) {
        return;
      }
      this.waveform.setRegionStart(value);
    });
    this.regionEndCtrl.valueChanges.subscribe((value) => {
      if (!this.waveform) {
        return;
      }
      this.waveform.setRegionEnd(
        this.duration > 0 && value > this.duration ? this.duration : value
      );
      this.regionsChangedFromTable = false;
    });

    this.regions.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value != null) {
        this.regionsChangedFromTable = true;
        this.regionStartCtrl.setValue(value.start);
        this.regionEndCtrl.setValue(value.end === 0 ? this.duration : value.end);
      }
    });

    this.srcAudioValue.pipe(takeUntil(this.ngUnsubscribe)).subscribe((src) => {
      if (src === true) {
        this.isAudioQueried = false;
        this.isLoaded = false;
      } else {
        this.callAudioWaveForm(src);
      }
    });

    this.messageNoAudio.pipe(takeUntil(this.ngUnsubscribe)).subscribe((message) => {
      if (message != null) {
        this.messageNoAudioAvailable = message;
      }
    });
  }
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }

  callAudioWaveForm(srcAudioValue: string) {
    this.hour = ('0' + this.tabIndex.toString()).slice(-2);
    this.isAudioQueried = !!srcAudioValue;
    this.isLoaded = false;
    this.play = false;
    this.regionStartCtrl.setValue(0);
    this.regionEndCtrl.setValue(0);
    if (srcAudioValue != null) {
      this.src = srcAudioValue;
    } else {
      this.src = null;
    }
  }

  forwardTenSecond() {

    const isPlayingBeforeTheStop = this.play;

    this.waveform.pause();
    this.waveform[currentTimeInnerWave] =
      this.waveform[currentTimeInnerWave] + 10 <= this.duration
        ? this.waveform[currentTimeInnerWave] + 10
        : this.duration;
    this.waveform[savedCurrentTimeInnerWave] = this.waveform[currentTimeInnerWave];
    if (isPlayingBeforeTheStop) {
      this.onPlayButtonClick();
    }
  }

  replayTenSeconds() {
    const isPlayingBeforeTheStop = this.play;
    this.waveform.pause();
    this.waveform[currentTimeInnerWave] =
      this.waveform[currentTimeInnerWave] - 10 >= 0
        ? this.waveform[currentTimeInnerWave] - 10
        : 0;
    this.waveform[savedCurrentTimeInnerWave] = this.waveform[currentTimeInnerWave];
    if (isPlayingBeforeTheStop) {
      this.onPlayButtonClick();
    }
  }

  onPlayButtonClick() {
    this.waveform.play();
    this.play = true;
  }

  onPauseButtonClick() {
    this.waveform.pause();
  }

  onTrackLoaded(time: number) {
    this.trackLoadTime = time;
  }

  onTrackRendered(time: number) {
    this.renderingTime = time;
    this.isLoaded = true;
    this.isAudioQueried = true;
  }

  onDurationChange(duration: number) {
    this.duration = duration;
    this.displayDuration = this.formatSecondsToDuration(duration);
  }

  onTimeUpdate(event: ITimeUpdateEvent) {
    this.currentTime = event;
    this.displayCurrentTime = this.formatSecondsToDuration(this.currentTime.time);
  }

  onPaused() {
    this.play = false;
  }

  onRegionChange(region: IRegionPositions) {
    this.regionPositions = region;
    if (!this.regionsChangedFromTable) {
      this.regionChange.emit({ ...this.regionPositions, duration: this.duration });
    }

    this.start = this.formatSecondsToDuration(this.regionPositions.start);
    this.end = this.formatSecondsToDuration(this.regionPositions.end);
    this.selectDuration = this.formatSecondsToDuration(this.regionPositions.end - this.regionPositions.start);
  }

  onBackwardButtonClick() {
    this.previousHour.emit(true);
  }

  onForwardButtonClick() {
    this.nextHour.emit(true);
  }

  isClearButtonDisabled(): boolean{
    return !this.selectedLinesOnTableInteraction?.length && this.regionPositions.start === 0 && this.regionPositions.end >= this.duration;
  }

  onClearSelection() {
    this.regionStartCtrl.setValue(0);
    this.regionEndCtrl.setValue(this.duration);
    this.selectedLinesOnTableInteraction = [];
    this.clearSelection.emit(true);
  }

  private formatSecondsToDuration(seconds: number): string {
    return moment.utc(seconds * 1000).format('mm:ss');
  }
}
